import React from 'react';
import { Map as NewbuildingMapComponent } from '@cian/frontend-newbuilding-map-component';
import { TInfrastructureItem, TRegularInfrastructureItem, TTAInfrastructureItem } from '../../../types/map';
import { trackBuildRouteClick, trackBuildRouteSuccess } from '../analytics';
import {
  checkIsInfrastructureRoute,
  getCurrentRouteItem,
  isTransportAccessibilityInfrastructureItem,
} from '../helpers';
import { IRoutingBuildSuccessParams } from '../../../components/Map';
import { getTelemetry } from '@cian/telemetry/browser';
import { YANDEX_API_TELEMETRY_KEY } from '../../../constants/map';
import { useTransportAccessibilityRouting } from './useTransportAccessibilityRouting';
import { useSelector } from 'react-redux';
import { getGeoCoordinates, getNewbuildingDisplayName } from '../../../selectors/newbuilding';

interface IUseRoutingParams {
  handleCloseOnboarding: () => void;
  hasFutureInfrastructure: boolean;
  mapApi: NewbuildingMapComponent | null;
  regularInfrastructureType: string | undefined;
}

export function useRouting({
  handleCloseOnboarding,
  hasFutureInfrastructure,
  mapApi,
  regularInfrastructureType,
}: IUseRoutingParams) {
  const { setTARouting, resetTARouting } = useTransportAccessibilityRouting(mapApi);

  const currentRouteItemRef = React.useRef<TRegularInfrastructureItem | null>(null);

  const pinCoordinates = useSelector(getGeoCoordinates);
  const newbuildingName = useSelector(getNewbuildingDisplayName);

  const setRoutingStart = React.useCallback(
    (geoItem?: TRegularInfrastructureItem) => {
      if (mapApi) {
        const from = pinCoordinates ? { coordinates: pinCoordinates, fixed: true, label: newbuildingName } : undefined;
        const to = geoItem ? { coordinates: geoItem.coordinates } : undefined;

        mapApi.startRouting(from, to, 'properties.name === "main" && geometry.type === "Polygon"');
      }
    },
    [pinCoordinates, mapApi, newbuildingName],
  );

  const handleBalloonRouteButtonClick = React.useCallback(
    (item: TInfrastructureItem) => {
      handleCloseOnboarding();

      if (isTransportAccessibilityInfrastructureItem(item)) {
        setTARouting(item as TTAInfrastructureItem);
      } else {
        currentRouteItemRef.current = item as TRegularInfrastructureItem;
        setRoutingStart(item as TRegularInfrastructureItem);
        trackBuildRouteClick(regularInfrastructureType);
      }
    },
    [handleCloseOnboarding, setRoutingStart],
  );

  const handleRoutingClick = React.useCallback(() => {
    handleCloseOnboarding();
    setRoutingStart();
    trackBuildRouteClick(regularInfrastructureType);
  }, [handleCloseOnboarding, setRoutingStart]);

  const handleRoutingBuildInit = React.useCallback(() => {
    getTelemetry().increment(YANDEX_API_TELEMETRY_KEY, 2);
  }, []);

  const handleRouteBuildingSuccess = React.useCallback(
    (params: IRoutingBuildSuccessParams) => {
      if (params.routes.length) {
        const infrastructurePoint = currentRouteItemRef.current;

        const isObjectPoint = !!infrastructurePoint && checkIsInfrastructureRoute(infrastructurePoint, params);
        const isFutureObject = infrastructurePoint ? 'constructionYearFrom' in infrastructurePoint : false;

        currentRouteItemRef.current = getCurrentRouteItem(currentRouteItemRef.current, isObjectPoint);

        trackBuildRouteSuccess({
          searchResult: params,
          objectType: regularInfrastructureType,
          isObjectPoint,
          isFutureObject,
          hasFutureInfrastructure,
        });
      }
    },
    [hasFutureInfrastructure],
  );

  return {
    handleBalloonRouteButtonClick,
    handleRoutingClick,

    handleRoutingBuildInit,
    handleRouteBuildingSuccess,

    resetTARouting,
  };
}
